import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Package, Lightbulb } from 'lucide-react';
import { useToast } from '../hooks/useToast';
import { useSugerencias } from '../hooks/useMermas';
import type { Ingrediente } from '../types/ingredientes';
import type { SugerenciaMerma } from '../types/mermas';
import { formatearFechaCorta } from '../utils/dateUtils';

export interface DatosMermaRapida {
  ingredienteId: string;
  nombreIngrediente: string;
  loteId: number | null;
  numeroLote: string;
  cantidad: number;
  motivo: string;
  observaciones?: string;
}

interface ModalMermaRapidaProps {
  isOpen: boolean;
  onClose: () => void;
  ingrediente: Ingrediente | null;
  onSubmit: (datos: DatosMermaRapida) => void;
}

const MOTIVOS_DISPONIBLES = [
  'Vencimiento',
  'Deterioro',
  'Contaminación',
  'Daño físico',
  'Error de manipulación',
  'Calidad deficiente',
  'Exceso de stock',
  'Derrame',
  'Mal almacenamiento',
  'Otro',
];

const ModalMermaRapida: React.FC<ModalMermaRapidaProps> = ({
  isOpen,
  onClose,
  ingrediente,
  onSubmit,
}) => {
  const { error } = useToast();

  // Obtener sugerencias reales de la API
  const {
    data: sugerenciasData,
    isLoading: cargandoSugerencias,
    error: errorSugerencias,
  } = useSugerencias({});

  const [cantidad, setCantidad] = useState<string>('');
  const [motivo, setMotivo] = useState<string>('');
  const [observaciones, setObservaciones] = useState<string>('');
  const [mostrarSugerencias, setMostrarSugerencias] = useState(true);

  // Filtrar sugerencias por el ingrediente seleccionado
  const sugerencias =
    sugerenciasData?.filter(
      (s) => ingrediente && s.ingrediente_id === parseInt(ingrediente.id)
    ) || [];

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setCantidad('');
      setMotivo('');
      setObservaciones('');
      setMostrarSugerencias(true);
    }
  }, [isOpen]);

  const handleSugerenciaClick = (sugerencia: SugerenciaMerma) => {
    setCantidad(sugerencia.cantidad_disponible.toString());
    setMotivo(sugerencia.motivo_sugerido);
    setObservaciones(
      `Lote ${sugerencia.numero_lote} - Vence: ${formatearFechaCorta(sugerencia.fecha_vencimiento)}`
    );
    setMostrarSugerencias(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!ingrediente || !cantidad || !motivo) {
      error(
        'Campos incompletos',
        'Por favor complete todos los campos obligatorios'
      );
      return;
    }

    const cantidadNum = parseFloat(cantidad);
    if (cantidadNum <= 0) {
      error('Cantidad inválida', 'La cantidad debe ser mayor a 0');
      return;
    }

    const datos: DatosMermaRapida = {
      ingredienteId: ingrediente.id,
      nombreIngrediente: ingrediente.nombre,
      loteId: null, // No usamos lotes específicos en merma rápida
      numeroLote: 'RAPIDA', // Identificador especial para merma rápida
      cantidad: cantidadNum,
      motivo,
      observaciones: observaciones || undefined,
    };

    onSubmit(datos);
    onClose();
  };

  if (!isOpen || !ingrediente) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Package className="h-6 w-6 text-red-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Registrar Merma
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {ingrediente.nombre}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Sugerencias automáticas */}
          {mostrarSugerencias && (
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <Lightbulb className="h-5 w-5 text-amber-600" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Sugerencias Inteligentes
                </h3>
              </div>

              {cargandoSugerencias ? (
                <div className="flex items-center justify-center p-8 border rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Analizando ingrediente...
                    </p>
                  </div>
                </div>
              ) : errorSugerencias ? (
                <div className="flex items-center p-4 border rounded-lg bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-sm text-red-700 dark:text-red-400">
                    No se pudieron cargar las sugerencias automáticas
                  </p>
                </div>
              ) : sugerencias.length === 0 ? (
                <div className="flex items-center p-4 border rounded-lg bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <Package className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-sm text-green-700 dark:text-green-400">
                    No hay sugerencias automáticas para este ingrediente. El
                    stock parece estar en buen estado.
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {sugerencias.map((sugerencia) => (
                    <div
                      key={sugerencia.lote_id}
                      onClick={() => handleSugerenciaClick(sugerencia)}
                      className={`cursor-pointer p-4 border-2 rounded-lg transition-colors ${
                        sugerencia.prioridad === 'alta'
                          ? 'border-red-200 bg-red-50 hover:bg-red-100 dark:border-red-800 dark:bg-red-900/20 dark:hover:bg-red-900/30'
                          : sugerencia.prioridad === 'media'
                            ? 'border-amber-200 bg-amber-50 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:hover:bg-amber-900/30'
                            : 'border-gray-200 bg-gray-50 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              sugerencia.prioridad === 'alta'
                                ? 'bg-red-500'
                                : sugerencia.prioridad === 'media'
                                  ? 'bg-amber-500'
                                  : 'bg-gray-500'
                            }`}
                          />
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {sugerencia.motivo_sugerido}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {sugerencia.cantidad_disponible}{' '}
                              {sugerencia.unidad_medida}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Lote: {sugerencia.numero_lote} - Vence:{' '}
                              {formatearFechaCorta(
                                sugerencia.fecha_vencimiento
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center space-x-1 text-sm text-gray-600 dark:text-gray-400">
                            <Package className="h-4 w-4" />
                            <span>Stock: {sugerencia.cantidad_disponible}</span>
                          </div>
                          {sugerencia.dias_para_vencimiento <= 7 && (
                            <div className="flex items-center space-x-1 text-sm text-red-600 dark:text-red-400">
                              <AlertTriangle className="h-4 w-4" />
                              <span>
                                {sugerencia.dias_para_vencimiento} días para
                                vencer
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setMostrarSugerencias(false)}
                className="mt-4 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Registrar merma manual
              </button>
            </div>
          )}

          {/* Formulario manual */}
          {!mostrarSugerencias && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Cantidad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cantidad *
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    value={cantidad}
                    onChange={(e) => setCantidad(e.target.value)}
                    min="0"
                    step="0.01"
                    required
                    className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Cantidad de merma"
                  />
                  <div className="flex items-center px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300">
                    {ingrediente.unidadMedida.abreviacion}
                  </div>
                </div>
              </div>

              {/* Motivo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo *
                </label>
                <select
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                  required
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Seleccionar motivo</option>
                  {MOTIVOS_DISPONIBLES.map((mot) => (
                    <option key={mot} value={mot}>
                      {mot}
                    </option>
                  ))}
                </select>
              </div>

              {/* Observaciones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observaciones
                </label>
                <textarea
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  rows={3}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="Observaciones adicionales (opcional)"
                />
              </div>

              {/* Botones */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setMostrarSugerencias(true)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Ver sugerencias
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Registrar Merma
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalMermaRapida;
